import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Printer, Mail, MessageSquare } from 'lucide-react';
import { useRestaurant } from '../../context/RestaurantContext';
import { alertDialog } from '../../components/ui/confirm-dialog';
import apiClient from '../../services/api';

/** Reservation Insights: one booking report per route, all from the insights endpoint with a date filter. */

// report key → [title, blurb]. Order = the tab strip + sidebar order.
export const RESERVATION_REPORTS = [
  ['overview', 'Overview', 'Bookings, website share, cancellation and no-show rates — with weekly channel mix.'],
  ['cancellations', 'Cancellations & No-shows', "Cancel vs no-show rate per channel — cancellations are recoverable seats, no-shows are dead seats."],
  ['funnel', 'Booking Funnel', 'Requests → confirmed → actually seated, as counts and % of requests.'],
  ['lead-time', 'No-show by Lead Time', 'No-show rate by how far ahead the booking was made — far-ahead bookings forget most.'],
  ['heatmap', 'Demand Heatmap', 'Bookings per 2-hour slot × weekday — spot the peak cells worth gating and the quiet ones worth promoting.'],
  ['economics', 'Channel Economics', 'Per channel: bookings, average party, cancel/no-show rates, buffet mix and paid booking amounts.'],
];

const CHANNELS = [
  ['ONLINE', 'Website', '#2a78d6'],
  ['PHONE', 'Phone', '#008300'],
  ['RECEPTION', 'Reception', '#898781'],
];

const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };

const Kpi = ({ label, value, sub, subTone }) => (
  <div>
    <div className="text-sm text-slate-500">{label}</div>
    <div className="text-3xl font-bold text-slate-900 mt-1">{value}</div>
    {sub && (
      <div className={`text-xs mt-1 ${
        subTone === 'bad' ? 'text-red-600' : subTone === 'good' ? 'text-blue-600' : 'text-slate-400'
      }`}>{sub}</div>
    )}
  </div>
);

const HBar = ({ label, pct, display, color = '#2a78d6' }) => (
  <div className="flex items-center gap-2 text-sm">
    <div className="w-28 shrink-0 truncate text-slate-500" title={label}>{label}</div>
    <div className="flex-1 h-3.5 rounded-full bg-slate-100 overflow-hidden">
      <div className="h-3.5 rounded-full" style={{ width: `${Math.max(1, Math.min(100, pct))}%`, background: color }} />
    </div>
    <div className="w-28 shrink-0 text-right font-medium tabular-nums">{display}</div>
  </div>
);

export default function ReservationInsights() {
  const { report = 'overview' } = useParams();
  const { restaurantId } = useRestaurant();
  const [dateFrom, setDateFrom] = useState(iso(daysAgo(29)));
  const [dateTo, setDateTo] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [, reportTitle, reportBlurb] =
    RESERVATION_REPORTS.find(([k]) => k === report) || RESERVATION_REPORTS[0];

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    apiClient.get(`/users/restaurant/${restaurantId}/reservations/insights/`, {
      params: { date_from: dateFrom, date_to: dateTo },
    })
      .then(({ data: d }) => {
        if (!d?.success) throw new Error(d?.error || 'Failed to load insights');
        setData(d.data);
        setError('');
      })
      .catch((e) => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [restaurantId, dateFrom, dateTo]);

  const t = data?.totals || {};
  const ch = data?.by_channel || {};
  const weekMax = Math.max(1, ...((data?.by_week || []).map((w) => w.online + w.phone + w.reception)));
  const heatMax = Math.max(1, ...(((data?.heatmap?.rows) || []).flatMap((r) => r.counts)));
  const inputCls = 'rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white';

  // Mock delivery: nothing is really sent yet, and the PDF option prints the page.
  const sendMock = (via) => {
    alertDialog({
      title: `Report ${via === 'email' ? 'emailed' : 'sent via SMS'} (mock)`,
      description: `"${reportTitle}" for ${dateFrom} → ${dateTo} was queued to the restaurant's registered ${via === 'email' ? 'email address' : 'mobile number'}. This is a mock — connect an ${via === 'email' ? 'email' : 'SMS'} gateway to send for real.`,
    });
  };

  return (
    <div className="p-6 w-full">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <div>
          <h1 className="font-display text-2xl font-semibold">{reportTitle}</h1>
        </div>
        {/* Export / share actions */}
        <div className="flex items-center gap-2 print:hidden">
          <button type="button" onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <Printer size={15} /> Print / PDF
          </button>
          <button type="button" onClick={() => sendMock('email')}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <Mail size={15} /> Email
          </button>
          <button type="button" onClick={() => sendMock('sms')}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <MessageSquare size={15} /> SMS
          </button>
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-4">{reportBlurb}</p>

      {/* Filters — date range drives every report; prior period of equal length powers deltas */}
      <div className="flex flex-wrap items-center gap-2 mb-6 text-sm text-slate-600 print:hidden">
        <label className="inline-flex items-center gap-1.5">From
          <input type="date" className={inputCls} value={dateFrom} max={dateTo}
            onChange={(e) => setDateFrom(e.target.value)} />
        </label>
        <label className="inline-flex items-center gap-1.5">To
          <input type="date" className={inputCls} value={dateTo} min={dateFrom}
            onChange={(e) => setDateTo(e.target.value)} />
        </label>
        {[7, 30, 90].map((n) => (
          <button key={n} type="button"
            onClick={() => { setDateFrom(iso(daysAgo(n - 1))); setDateTo(iso(new Date())); }}
            className="px-3 py-1.5 rounded-full text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50">
            Last {n} days
          </button>
        ))}
      </div>

      {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">{error}</div>}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 py-16 justify-center">
          <Loader2 size={18} className="animate-spin" /> Loading insights…
        </div>
      ) : data && (
        <>
          {/* ─── Overview: KPIs + weekly channel mix ─── */}
          {report === 'overview' && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                <Kpi label={`Bookings · ${data.days} days`} value={t.bookings ?? 0}
                  sub={t.bookings_delta_pct != null ? `${t.bookings_delta_pct > 0 ? '+' : ''}${t.bookings_delta_pct}% vs prior period` : 'no prior-period data'}
                  subTone={t.bookings_delta_pct >= 0 ? 'good' : 'bad'} />
                <Kpi label="Website share" value={`${t.online_share ?? 0}%`}
                  sub={t.prior_online_share != null ? `was ${t.prior_online_share}% in the prior period` : null} />
                <Kpi label="Cancellation rate" value={`${t.cancel_rate ?? 0}%`} sub={`${t.cancelled ?? 0} bookings`} />
                <Kpi label="No-show rate" value={`${t.no_show_rate ?? 0}%`}
                  sub={`${t.no_show_seats ?? 0} seats lost`} subTone={t.no_show_seats > 0 ? 'bad' : undefined} />
              </div>

              <h2 className="font-semibold text-slate-900 mb-1">Bookings by channel · weekly</h2>
              <div className="flex items-center gap-4 text-xs text-slate-500 mb-2">
                {CHANNELS.map(([k, l, c]) => (
                  <span key={k} className="inline-flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ background: c }} /> {l}
                  </span>
                ))}
              </div>
              <div className="space-y-2">
                {(data.by_week || []).map((w) => {
                  const total = w.online + w.phone + w.reception;
                  return (
                    <div key={w.from} className="flex items-center gap-2 text-sm">
                      <div className="w-24 shrink-0 text-slate-500">Wk of {w.label}</div>
                      <div className="flex-1 flex h-5 rounded overflow-hidden bg-slate-100">
                        {CHANNELS.map(([k, l, c]) => {
                          const v = w[k.toLowerCase()];
                          return v > 0 ? (
                            <div key={k} title={`${l}: ${v}`}
                              style={{ width: `${(v / weekMax) * 100}%`, background: c }} />
                          ) : null;
                        })}
                      </div>
                      <div className="w-12 shrink-0 text-right font-medium tabular-nums">{total}</div>
                    </div>
                  );
                })}
                {(data.by_week || []).length === 0 && (
                  <p className="text-sm text-slate-400">No bookings in this range.</p>
                )}
              </div>
            </>
          )}

          {/* ─── Cancellations & no-shows by channel ─── */}
          {report === 'cancellations' && (
            <div className="space-y-5">
              {CHANNELS.map(([k, l]) => (
                <div key={k}>
                  <div className="text-sm font-semibold text-slate-700 mb-1.5">{l} · {ch[k]?.bookings ?? 0} bookings</div>
                  <div className="space-y-1.5">
                    <HBar label="Cancelled" pct={ch[k]?.cancel_rate ?? 0} display={`${ch[k]?.cancel_rate ?? 0}%`} color="#eda100" />
                    <HBar label="No-show" pct={ch[k]?.no_show_rate ?? 0} display={`${ch[k]?.no_show_rate ?? 0}%`} color="#d03b3b" />
                  </div>
                </div>
              ))}
              <p className="text-xs text-slate-400">
                A cancellation frees the seat; a no-show wastes it.
              </p>
            </div>
          )}

          {/* ─── Funnel ─── */}
          {report === 'funnel' && (
            <div className="space-y-2">
              {(data.funnel || []).map((f) => (
                <HBar key={f.label} label={f.label}
                  pct={(f.count / Math.max(1, data.funnel?.[0]?.count || 0)) * 100}
                  display={`${f.count} · ${f.pct}%`} />
              ))}
              <p className="text-xs text-slate-400 pt-2">
                Confirmed minus seated is the no-show gap.
              </p>
            </div>
          )}

          {/* ─── No-show by lead time ─── */}
          {report === 'lead-time' && (
            <div className="space-y-2">
              {(data.no_show_by_lead_time || []).map((b) => (
                <HBar key={b.label} label={b.label} pct={b.no_show_rate}
                  display={`${b.no_show_rate}% of ${b.bookings}`} color="#d03b3b" />
              ))}
              <p className="text-xs text-slate-400 pt-2">
                The further ahead a booking, the higher its no-show risk.
              </p>
            </div>
          )}

          {/* ─── Demand heatmap ─── */}
          {report === 'heatmap' && (
            <>
              <table className="w-full text-sm text-center border-separate" style={{ borderSpacing: '3px 3px' }}>
                <thead>
                  <tr>
                    <th className="text-left font-medium text-slate-500 pr-2">Slot</th>
                    {(data.heatmap?.days || []).map((d) => <th key={d} className="font-medium text-slate-500">{d}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {(data.heatmap?.rows || []).map((row) => (
                    <tr key={row.slot}>
                      <td className="text-left text-slate-600 pr-2 whitespace-nowrap">{row.slot}</td>
                      {row.counts.map((c, i) => (
                        <td key={i} className="rounded-md py-1.5 font-semibold"
                          title={`${row.slot} · ${data.heatmap.days[i]}: ${c} booking(s)`}
                          style={{
                            background: `rgba(37, 99, 235, ${c ? 0.15 + 0.85 * (c / heatMax) : 0.05})`,
                            color: c / heatMax > 0.55 ? '#fff' : '#1e293b',
                          }}>
                          {c}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-slate-400 mt-2">
                Darker means more bookings. Gate the dark cells, promote the pale.
              </p>
            </>
          )}

          {/* ─── Channel economics ─── */}
          {report === 'economics' && (
            <>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-2 font-medium">Channel</th>
                    <th className="py-2 font-medium text-right">Bookings</th>
                    <th className="py-2 font-medium text-right">Avg party</th>
                    <th className="py-2 font-medium text-right">Cancel</th>
                    <th className="py-2 font-medium text-right">No-show</th>
                    <th className="py-2 font-medium text-right">Buffet mix</th>
                    <th className="py-2 font-medium text-right">Avg booking amount</th>
                  </tr>
                </thead>
                <tbody>
                  {CHANNELS.map(([k, l]) => (
                    <tr key={k} className="border-b border-slate-100">
                      <td className="py-2 text-slate-800 font-medium">{l}</td>
                      <td className="py-2 text-right tabular-nums">{ch[k]?.bookings ?? 0}</td>
                      <td className="py-2 text-right tabular-nums">{ch[k]?.avg_party ?? 0}</td>
                      <td className="py-2 text-right tabular-nums">{ch[k]?.cancel_rate ?? 0}%</td>
                      <td className="py-2 text-right tabular-nums">{ch[k]?.no_show_rate ?? 0}%</td>
                      <td className="py-2 text-right tabular-nums">{ch[k]?.buffet_mix ?? 0}%</td>
                      <td className="py-2 text-right tabular-nums">
                        {ch[k]?.avg_booking_amount != null
                          ? `Rs ${Number(ch[k].avg_booking_amount).toLocaleString('en-MU')}`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-slate-400 mt-2">
                Booking deposits only — dining revenue is on Sales.
              </p>
            </>
          )}
        </>
      )}
    </div>
  );
}
